import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import { Button } from "../../../components/ui/common/button";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import LoadingOverlay from "../ingredientList/components/LoadingOverlay";
import CalculateConfirmDialog from "./components/CalculateConfirmDialog";
import EnvironmentalDataCard from "./components/EnvironmentalDataCard";
import IngredientInfoCard from "./components/IngredientInfoCard";
import IngredientRegisterFooter from "./components/IngredientRegisterFooter";
import NewSupplierFormPanel from "./components/NewSupplierFormPanel";
import RegisterDataSections from "./components/RegisterDataSections";
import RegisterFormProvider from "./components/RegisterFormProvider";
import UnitDetailsCard from "./components/UnitDetailsCard";
import { useIngredientRegisterViewModel } from "./hooks/useIngredientRegisterViewModel";

interface IngredientRegisterModalProps {
  visibleModal: boolean;
  setVisibleModal: (value: boolean) => void;
  ingredientList?: unknown[];
  refetchIngredients?: () => void;
}

/**
 * Tailwind rebuild of the legacy `IngredientRegisterModalView`
 * (src/screens/ingredients/ingredientRegister/IngredientRegisterModalView.tsx),
 * matching the reference screenshot's 3-card layout (Ingredient Info / Unit
 * Details / Environmental Data). Structured like the codebase's other
 * Tailwind side-modal forms — `TailwindSideModal` + `CommonSplitContainer`
 * (scrollable top / fixed bottom) + `TailwindBottomButtonView` for the
 * primary Cancel/Save actions — see
 * src/screens/recipe_simulator/FilterVarientTableModal.tsx for the same
 * pattern. All state/data-fetching lives in `useIngredientRegisterViewModel`;
 * this component only composes presentation — same split as
 * `IngredientListNew.tsx`.
 *
 * Drop-in replacement for `IngredientRegisterModalView` wherever it's
 * rendered for the register flow (identical props). Packaging/Transportation
 * /LCA sections below the cards have also been rebuilt in Tailwind — see
 * `RegisterDataSections.tsx`; the "+ New supplier" sub-flow
 * (`NewSupplierFormPanel`) now renders the shared `SupplierRegisterFields`
 * (src/screens/lcaSupplier/shared/components/), matching the LCA Suppliers
 * prototype's card layout — still bound to the same protected legacy
 * `useSupplierRegisterForm`/`useSupplierSave` hooks underneath.
 */
const IngredientRegisterModal = ({
  visibleModal,
  setVisibleModal,
  refetchIngredients
}: IngredientRegisterModalProps) => {
  const vm = useIngredientRegisterViewModel({ visibleModal, setVisibleModal, refetchIngredients });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onCancel();
        }}
        title="Add Ingredients"
        description={vm.hideIngredientView ? "Add New Supplier" : "General Details"}
        onOpenAutoFocus={e => e.preventDefault()}
        contentClassName="tw-bg-[#FBFAF7]"
      >
        <CommonSplitContainer
          currentHeight={0.9}
          overflowX="hidden"
          topChild={
            <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-3">
              {!vm.hideIngredientView ? <IngredientInfoCard vm={vm} /> : null}

              {vm.isLoadingSupplierData ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-8 tw-text-sm tw-text-muted-foreground">
                  Loading...
                </div>
              ) : vm.newSupplier ? (
                <NewSupplierFormPanel vm={vm} />
              ) : (
                <RegisterFormProvider key={vm.formSyncKey} control={vm.control} errors={vm.errors}>
                  <UnitDetailsCard vm={vm} />
                  <EnvironmentalDataCard vm={vm} />
                </RegisterFormProvider>
              )}

              <RegisterDataSections vm={vm} />
            </div>
          }
          bottomChild={
            <>
              {vm.isIngredientMappingMode && vm.enableIngredientMapping ? (
                <div className="tw-flex tw-justify-end tw-border-t tw-border-border tw-px-6 tw-pt-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={vm.enableIngredientButton}
                    onClick={vm.onNavigateIngredientMapping}
                  >
                    Ingredient Mapping
                  </Button>
                </div>
              ) : null}
              <IngredientRegisterFooter vm={vm} />
            </>
          }
        />
      </TailwindSideModal>

      <CalculateConfirmDialog vm={vm} />

      {vm.isLoadingPutDataToQueue ? <LoadingOverlay /> : null}
    </>
  );
};

export default IngredientRegisterModal;
